import { useState } from "react";
import {
  ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
  BarChart, Bar, CartesianGrid, Cell, ReferenceLine, Legend
} from "recharts";

// ─── MOCK DATA ────────────────────────────────────────────────────────────────

const FRAUD_SIGNALS = [
  {
    id: "FS-2025-0041",
    operator: "Lucky Star Casino",
    type: "Revenue Concealment",
    severity: "CRITICAL",
    confidence: 94,
    detectedAt: "Dec 15, 2025 09:14",
    status: "UNDER INVESTIGATION",
    description: "Systematic revenue underreporting detected across 6 consecutive reporting periods. Operator-reported figures consistently 38–42% below payment processor data. Pattern consistent with deliberate concealment.",
    indicators: [
      { label: "Revenue Gap", value: "40.0%", threshold: ">10%", breach: true },
      { label: "Pattern Consistency", value: "6/6 periods", threshold: "<2 periods", breach: true },
      { label: "Reporting Delay", value: "3.2 days avg", threshold: ">2 days", breach: true },
      { label: "Round-Number Bias", value: "67%", threshold: ">40%", breach: true },
    ],
    mlScore: 94,
    radarData: [
      { axis: "Revenue Gap", value: 94 }, { axis: "Timing", value: 78 },
      { axis: "Round Number", value: 67 }, { axis: "Payout Ratio", value: 82 },
      { axis: "Cross-Operator", value: 55 },
    ],
  },
  {
    id: "FS-2025-0038",
    operator: "Galaxy Gaming",
    type: "Unauthorised Operations",
    severity: "CRITICAL",
    confidence: 99,
    detectedAt: "Nov 28, 2025 16:45",
    status: "LICENCE SUSPENDED",
    description: "Confirmed evidence of operating outside licensed scope. Transaction data shows revenue from unlicensed game types accounting for 34% of total. Cross-referenced with 1XBet API footprint. License suspended Dec 28, 2025.",
    indicators: [
      { label: "Unlicensed Revenue %", value: "34%", threshold: "0%", breach: true },
      { label: "External API Calls", value: "1,847/day", threshold: "0", breach: true },
      { label: "Revenue Mismatch", value: "91%", threshold: "<10%", breach: true },
      { label: "KYB Ownership Gap", value: "6 owners unverified", threshold: "0", breach: true },
    ],
    mlScore: 99,
    radarData: [
      { axis: "Revenue Gap", value: 99 }, { axis: "Timing", value: 90 },
      { axis: "Round Number", value: 45 }, { axis: "Payout Ratio", value: 95 },
      { axis: "Cross-Operator", value: 88 },
    ],
  },
  {
    id: "FS-2025-0033",
    operator: "Premier Lotto",
    type: "Round Number Bias",
    severity: "MEDIUM",
    confidence: 78,
    detectedAt: "Dec 12, 2025 11:02",
    status: "MONITORING",
    description: "Statistical analysis reveals that 72% of submitted revenue figures end in 0,000 or 5,000. Probability of natural occurrence < 0.003. Suggests manual figure adjustment before submission rather than automated system reporting.",
    indicators: [
      { label: "Round-Number Frequency", value: "72%", threshold: ">40%", breach: true },
      { label: "Natural Probability", value: "0.003", threshold: ">0.05", breach: true },
      { label: "Revenue Gap", value: "2.1%", threshold: ">10%", breach: false },
      { label: "Submission Timing", value: "On-time", threshold: ">2 days", breach: false },
    ],
    mlScore: 78,
    radarData: [
      { axis: "Revenue Gap", value: 20 }, { axis: "Timing", value: 15 },
      { axis: "Round Number", value: 78 }, { axis: "Payout Ratio", value: 30 },
      { axis: "Cross-Operator", value: 10 },
    ],
  },
  {
    id: "FS-2025-0030",
    operator: "DoxxBet Liberia",
    type: "Cross-Border Routing",
    severity: "HIGH",
    confidence: 71,
    detectedAt: "Nov 30, 2025 14:22",
    status: "PENDING REVIEW",
    description: "Transaction routing analysis shows 23% of deposits originating from Slovak IBANs before conversion to LRD. No corresponding cross-border payment disclosure filed with NLA or FIA. Potential regulatory evasion pattern.",
    indicators: [
      { label: "Foreign-Origin Deposits", value: "23%", threshold: ">5%", breach: true },
      { label: "Disclosure Filed", value: "No", threshold: "Required", breach: true },
      { label: "Currency Conversion Events", value: "147", threshold: ">0 requires filing", breach: true },
      { label: "Beneficial Ownership", value: "Incomplete", threshold: "Full required", breach: true },
    ],
    mlScore: 71,
    radarData: [
      { axis: "Revenue Gap", value: 35 }, { axis: "Timing", value: 50 },
      { axis: "Round Number", value: 20 }, { axis: "Payout Ratio", value: 55 },
      { axis: "Cross-Operator", value: 71 },
    ],
  },
  {
    id: "FS-2025-0028",
    operator: "Monrovia Bet",
    type: "Suspicious Timing Pattern",
    severity: "LOW",
    confidence: 52,
    detectedAt: "Dec 10, 2025 08:30",
    status: "MONITORING",
    description: "Transaction submission timestamps cluster in a narrow 2-hour window (22:00–00:00) across all reporting days, inconsistent with natural betting activity spread. May indicate batch manual entry rather than real-time reporting.",
    indicators: [
      { label: "Timing Cluster", value: "22:00–00:00", threshold: "Spread expected", breach: true },
      { label: "Revenue Gap", value: "5.3%", threshold: ">10%", breach: false },
      { label: "Round-Number Bias", value: "18%", threshold: ">40%", breach: false },
      { label: "Submission Delay", value: "1.1 days avg", threshold: ">2 days", breach: false },
    ],
    mlScore: 52,
    radarData: [
      { axis: "Revenue Gap", value: 40 }, { axis: "Timing", value: 52 },
      { axis: "Round Number", value: 18 }, { axis: "Payout Ratio", value: 25 },
      { axis: "Cross-Operator", value: 15 },
    ],
  },
];

const HEATMAP_DATA = [
  { name: "Bet360", Mon: 12, Tue: 15, Wed: 11, Thu: 14, Fri: 18, Sat: 22, Sun: 19 },
  { name: "Lucky Star", Mon: 45, Tue: 48, Wed: 42, Thu: 50, Fri: 55, Sat: 61, Sun: 58 },
  { name: "Galaxy", Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0 },
  { name: "Premier", Mon: 28, Tue: 31, Wed: 27, Thu: 33, Fri: 38, Sat: 42, Sun: 35 },
  { name: "Monrovia", Mon: 22, Tue: 24, Wed: 21, Thu: 25, Fri: 29, Sat: 33, Sun: 27 },
  { name: "DoxxBet", Mon: 35, Tue: 38, Wed: 34, Thu: 40, Fri: 44, Sat: 49, Sun: 42 },
];

const ML_SCATTER = [
  { x: 12, y: 8, operator: "Bet360", risk: 18 },
  { x: 78, y: 88, operator: "Lucky Star", risk: 74 },
  { x: 95, y: 99, operator: "Galaxy", risk: 96 },
  { x: 32, y: 28, operator: "Premier", risk: 32 },
  { x: 22, y: 19, operator: "Monrovia", risk: 27 },
  { x: 55, y: 62, operator: "DoxxBet", risk: 55 },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const severityConfig = {
  CRITICAL: { color: "#FF4D4F", bg: "rgba(255,77,79,0.12)", border: "rgba(255,77,79,0.3)" },
  HIGH: { color: "#FF7A00", bg: "rgba(255,122,0,0.12)", border: "rgba(255,122,0,0.3)" },
  MEDIUM: { color: "#FAAD14", bg: "rgba(250,173,20,0.12)", border: "rgba(250,173,20,0.3)" },
  LOW: { color: "#8C8C8C", bg: "rgba(140,140,140,0.1)", border: "rgba(140,140,140,0.2)" },
};

const statusBadge = {
  "UNDER INVESTIGATION": { color: "#FF4D4F", bg: "rgba(255,77,79,0.1)" },
  "LICENCE SUSPENDED": { color: "#FF4D4F", bg: "rgba(255,77,79,0.1)" },
  "MONITORING": { color: "#FAAD14", bg: "rgba(250,173,20,0.1)" },
  "PENDING REVIEW": { color: "#FF7A00", bg: "rgba(255,122,0,0.1)" },
};

const heatColor = (val) => {
  if (val === 0) return "#141428";
  if (val < 20) return "#1a3a1a";
  if (val < 35) return "#2d5a1b";
  if (val < 50) return "#FAAD14";
  return "#FF4D4F";
};

// ─── SIGNAL CARD ─────────────────────────────────────────────────────────────

function SignalCard({ signal, selected, onClick }) {
  const sev = severityConfig[signal.severity];
  const isSelected = selected?.id === signal.id;

  return (
    <div onClick={onClick} style={{
      padding: "14px 16px", cursor: "pointer", borderRadius: 10,
      background: isSelected ? "rgba(64,169,255,0.06)" : "rgba(255,255,255,0.03)",
      border: isSelected ? "1px solid rgba(64,169,255,0.4)" : "1px solid rgba(255,255,255,0.07)",
      transition: "all 0.2s",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", color: "#595959" }}>{signal.id}</span>
        <span style={{
          fontSize: 10, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace",
          color: sev.color, background: sev.bg, padding: "2px 8px", borderRadius: 4,
        }}>{signal.severity}</span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#E8E8E8", marginBottom: 3 }}>{signal.operator}</div>
      <div style={{ fontSize: 11, color: "#8C8C8C", marginBottom: 10 }}>{signal.type}</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 10, color: "#595959" }}>Confidence</span>
          <div style={{ width: 60, height: 4, background: "rgba(255,255,255,0.06)", borderRadius: 2 }}>
            <div style={{ width: `${signal.confidence}%`, height: "100%", borderRadius: 2, background: sev.color }} />
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, color: sev.color, fontFamily: "'IBM Plex Mono', monospace" }}>{signal.confidence}%</span>
        </div>
        <span style={{
          fontSize: 10, color: (statusBadge[signal.status] || {}).color || "#8C8C8C",
          background: (statusBadge[signal.status] || {}).bg || "transparent",
          padding: "1px 7px", borderRadius: 3, fontFamily: "'IBM Plex Mono', monospace",
        }}>{signal.status}</span>
      </div>
    </div>
  );
}

// ─── DETAIL ───────────────────────────────────────────────────────────────────

function SignalDetail({ signal }) {
  const sev = severityConfig[signal.severity];

  return (
    <div style={{
      background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 12, padding: 22, height: "100%",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 11, color: "#595959", fontFamily: "'IBM Plex Mono', monospace", marginBottom: 4 }}>{signal.id} · {signal.detectedAt}</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#F0F0F0" }}>{signal.operator}</div>
          <div style={{ fontSize: 12, color: "#8C8C8C", marginTop: 2 }}>{signal.type}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: sev.color, fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1 }}>{signal.confidence}%</div>
          <div style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace" }}>ML CONFIDENCE</div>
        </div>
      </div>

      <div style={{ background: sev.bg, border: `1px solid ${sev.border}`, borderRadius: 8, padding: "12px 14px", marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: sev.color, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, marginBottom: 6, textTransform: "uppercase" }}>Detection Summary</div>
        <div style={{ fontSize: 12, color: "#A8A8A8", lineHeight: 1.6 }}>{signal.description}</div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Fraud Indicators</div>
        {signal.indicators.map(ind => (
          <div key={ind.label} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", marginBottom: 6,
            background: ind.breach ? "rgba(255,77,79,0.05)" : "rgba(82,196,26,0.05)",
            border: `1px solid ${ind.breach ? "rgba(255,77,79,0.15)" : "rgba(82,196,26,0.15)"}`,
            borderRadius: 6,
          }}>
            <span style={{ fontSize: 14, color: ind.breach ? "#FF4D4F" : "#52C41A" }}>{ind.breach ? "✗" : "✓"}</span>
            <span style={{ fontSize: 11, color: "#A8A8A8", flex: 1 }}>{ind.label}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: ind.breach ? "#FF4D4F" : "#52C41A", fontFamily: "'IBM Plex Mono', monospace" }}>{ind.value}</span>
            <span style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace" }}>threshold: {ind.threshold}</span>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>ML Fraud Signature</div>
        <ResponsiveContainer width="100%" height={160}>
          <RadarChart data={signal.radarData}>
            <PolarGrid stroke="rgba(255,255,255,0.06)" />
            <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10, fill: "#595959", fontFamily: "'IBM Plex Mono', monospace" }} />
            <Radar dataKey="value" stroke={sev.color} fill={sev.color} fillOpacity={0.15} strokeWidth={2} dot={{ r: 3, fill: sev.color }} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button style={{
          flex: 1, padding: "9px", borderRadius: 7, border: "none", cursor: "pointer",
          background: "rgba(255,77,79,0.15)", color: "#FF4D4F",
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em",
        }}>↗ Escalate to FIA</button>
        <button style={{
          flex: 1, padding: "9px", borderRadius: 7, border: "1px solid rgba(255,255,255,0.1)", cursor: "pointer",
          background: "transparent", color: "#8C8C8C",
          fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em",
        }}>⤓ Export Evidence</button>
      </div>
    </div>
  );
}

// ─── HEATMAP ──────────────────────────────────────────────────────────────────

function RiskHeatmap() {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: 24 }}>
      <div style={{ fontSize: 12, color: "#8C8C8C", marginBottom: 20, fontFamily: "'IBM Plex Mono', monospace" }}>Operator Risk Heatmap — Daily AML Score by Day of Week</div>
      <div style={{ display: "grid", gridTemplateColumns: "90px repeat(7, 1fr)", gap: 4 }}>
        <div />
        {days.map(d => <div key={d} style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace", textAlign: "center", marginBottom: 4 }}>{d}</div>)}
        {HEATMAP_DATA.map(row => (
          <>
            <div key={row.name + "-label"} style={{ fontSize: 11, color: "#8C8C8C", display: "flex", alignItems: "center" }}>{row.name}</div>
            {days.map(d => (
              <div key={d} style={{
                height: 34, borderRadius: 4, background: heatColor(row[d]),
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, color: row[d] > 30 ? "#F0F0F0" : "#595959",
                fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600,
              }}>
                {row[d] > 0 ? row[d] : "–"}
              </div>
            ))}
          </>
        ))}
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 16, alignItems: "center" }}>
        <span style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace" }}>RISK →</span>
        {[["#1a3a1a", "Low"], ["#2d5a1b", "Moderate"], ["#FAAD14", "High"], ["#FF4D4F", "Critical"]].map(([color, label]) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 12, height: 12, borderRadius: 2, background: color }} />
            <span style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace" }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ML SCATTER ───────────────────────────────────────────────────────────────

function MLScatterPlot() {
  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    const sColor = payload.risk >= 70 ? "#FF4D4F" : payload.risk >= 40 ? "#FAAD14" : "#52C41A";
    return (
      <g>
        <circle cx={cx} cy={cy} r={10} fill={sColor} fillOpacity={0.2} stroke={sColor} strokeWidth={1.5} />
        <text x={cx} y={cy - 14} textAnchor="middle" fill="#D9D9D9" fontSize={9} fontFamily="IBM Plex Mono, monospace">{payload.operator}</text>
      </g>
    );
  };

  return (
    <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: 24 }}>
      <div style={{ fontSize: 12, color: "#8C8C8C", marginBottom: 6, fontFamily: "'IBM Plex Mono', monospace" }}>ML Anomaly Isolation — Revenue Gap vs. Pattern Score</div>
      <div style={{ fontSize: 10, color: "#595959", marginBottom: 16, fontFamily: "'IBM Plex Mono', monospace" }}>Operators in the upper-right quadrant show compounding fraud signals</div>
      <ResponsiveContainer width="100%" height={240}>
        <ScatterChart margin={{ top: 20, right: 20, bottom: 10, left: -10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
          <XAxis dataKey="x" name="Revenue Gap Score" tick={{ fontSize: 10, fill: "#595959" }} label={{ value: "Revenue Gap →", position: "insideBottom", offset: -5, fill: "#595959", fontSize: 10 }} />
          <YAxis dataKey="y" name="Pattern Score" tick={{ fontSize: 10, fill: "#595959" }} label={{ value: "Pattern Score →", angle: -90, position: "insideLeft", fill: "#595959", fontSize: 10 }} />
          <ReferenceLine x={50} stroke="rgba(255,77,79,0.3)" strokeDasharray="4 4" />
          <ReferenceLine y={50} stroke="rgba(255,77,79,0.3)" strokeDasharray="4 4" />
          <Tooltip contentStyle={{ background: "#1A1A2E", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, fontSize: 11, fontFamily: "'IBM Plex Mono', monospace" }} cursor={{ stroke: "rgba(255,255,255,0.1)" }} />
          <Scatter data={ML_SCATTER} shape={<CustomDot />} />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────

export default function NLA_FraudDetection() {
  const [selected, setSelected] = useState(FRAUD_SIGNALS[0]);
  const [subView, setSubView] = useState("signals");

  const critical = FRAUD_SIGNALS.filter(s => s.severity === "CRITICAL").length;
  const underInvestigation = FRAUD_SIGNALS.filter(s => s.status === "UNDER INVESTIGATION").length;
  const avgConfidence = Math.round(FRAUD_SIGNALS.reduce((a, s) => a + s.confidence, 0) / FRAUD_SIGNALS.length);

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #0D0D1A 0%, #0A1628 50%, #0D0D1A 100%)",
      fontFamily: "'IBM Plex Sans', 'Segoe UI', sans-serif",
      color: "#D9D9D9", padding: 24,
    }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&family=IBM+Plex+Sans:wght@400;500;600;700;800&display=swap'); *{box-sizing:border-box;} ::-webkit-scrollbar{width:6px;} ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:3px;}`}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <div style={{ width: 6, height: 28, background: "linear-gradient(180deg, #FF4D4F, #820014)", borderRadius: 3 }} />
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#F0F0F0", letterSpacing: "-0.02em" }}>
              Fraud Detection Engine
            </h1>
          </div>
          <div style={{ fontSize: 11, color: "#595959", marginLeft: 16, fontFamily: "'IBM Plex Mono', monospace" }}>
            NLA · Operator-Level Fraud Signal Analysis · Dec 28, 2025
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {["signals", "heatmap", "ml"].map(v => (
            <button key={v} onClick={() => setSubView(v)} style={{
              background: subView === v ? "rgba(255,77,79,0.15)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${subView === v ? "rgba(255,77,79,0.5)" : "rgba(255,255,255,0.08)"}`,
              color: subView === v ? "#FF4D4F" : "#8C8C8C", cursor: "pointer",
              padding: "7px 14px", borderRadius: 7, fontSize: 11, fontWeight: 600,
              fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.07em",
              transition: "all 0.2s",
            }}>
              {v === "signals" ? "⚠ Fraud Signals" : v === "heatmap" ? "▦ Risk Heatmap" : "◎ ML Analysis"}
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Active Fraud Signals", value: FRAUD_SIGNALS.length, color: "#FAAD14" },
          { label: "Critical Severity", value: critical, color: "#FF4D4F" },
          { label: "Under Investigation", value: underInvestigation, color: "#FF7A00" },
          { label: "Avg ML Confidence", value: `${avgConfidence}%`, color: "#40A9FF" },
        ].map(item => (
          <div key={item.label} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, padding: "14px 18px" }}>
            <div style={{ fontSize: 10, color: "#595959", marginBottom: 6, fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.07em" }}>{item.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: item.color, fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1 }}>{item.value}</div>
          </div>
        ))}
      </div>

      {subView === "signals" && (
        <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
              Detected Signals ({FRAUD_SIGNALS.length})
            </div>
            {FRAUD_SIGNALS.map(s => (
              <SignalCard key={s.id} signal={s} selected={selected} onClick={() => setSelected(s)} />
            ))}
          </div>
          <div>{selected && <SignalDetail signal={selected} />}</div>
        </div>
      )}

      {subView === "heatmap" && <RiskHeatmap />}

      {subView === "ml" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <MLScatterPlot />
          <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: 24 }}>
            <div style={{ fontSize: 12, color: "#8C8C8C", marginBottom: 16, fontFamily: "'IBM Plex Mono', monospace" }}>ML Model Performance Metrics</div>
            {[
              { label: "Model Type", value: "Isolation Forest + Rules Engine" },
              { label: "Training Window", value: "18 months (Jul 2024 – Dec 2025)" },
              { label: "False Positive Rate", value: "4.2%", color: "#52C41A" },
              { label: "Detection Accuracy", value: "91.8%", color: "#52C41A" },
              { label: "Avg Detection Lag", value: "2.4 hours", color: "#52C41A" },
              { label: "vs. Manual Audit", value: "90 days → 2.4 hours", color: "#FAAD14" },
              { label: "Last Model Update", value: "Dec 24, 2025" },
              { label: "Next Scheduled Retrain", value: "Jan 7, 2026" },
            ].map(item => (
              <div key={item.label} style={{ display: "flex", justifyContent: "space-between", padding: "9px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                <span style={{ fontSize: 11, color: "#595959", fontFamily: "'IBM Plex Mono', monospace" }}>{item.label}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: item.color || "#D9D9D9", fontFamily: "'IBM Plex Mono', monospace" }}>{item.value}</span>
              </div>
            ))}
            <div style={{
              marginTop: 16, background: "rgba(82,196,26,0.06)", border: "1px solid rgba(82,196,26,0.2)",
              borderRadius: 8, padding: "12px 14px",
            }}>
              <div style={{ fontSize: 11, color: "#52C41A", fontFamily: "'IBM Plex Mono', monospace", marginBottom: 6, fontWeight: 700 }}>LIPAY Crisis Benchmark</div>
              <div style={{ fontSize: 12, color: "#8C8C8C", lineHeight: 1.6 }}>
                In the Dec 2024 LIPAY crisis, issues went undetected for <strong style={{ color: "#FF4D4F" }}>8–10 weeks</strong>. 
                This ML engine would have surfaced the anomaly in <strong style={{ color: "#52C41A" }}>~2.4 hours</strong> — 
                a <strong style={{ color: "#52C41A" }}>98.6% reduction</strong> in detection lag.
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 20, textAlign: "center", fontSize: 10, color: "#333", fontFamily: "'IBM Plex Mono', monospace" }}>
        GCRMS · XI CONSULT CORP · CONFIDENTIAL — NLA DEMO BUILD
      </div>
    </div>
  );
}